import { useReducer, useEffect } from 'react';

import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { fg } from '@atlaskit/platform-feature-flags';

import type { AIPlugin } from '../../../aiPluginType';

export type State = 'discard' | 'loading' | 'idle' | 'error';

export type Action =
	| { type: 'discard' }
	| { type: 'cancel' }
	| { type: 'reset' }
	| { type: 'terminate' };

interface UIState {
	discard: boolean;
	terminated: boolean;
}

const initialState: UIState = {
	discard: false,
	terminated: false,
};

function reducer(state: UIState, action: Action): UIState {
	switch (action.type) {
		case 'discard':
			return { ...state, discard: true };
		case 'cancel':
			return { ...state, discard: false };
		case 'terminate':
			return { ...state, terminated: true };
		case 'reset':
			return { discard: false, terminated: false };
		default:
			return state;
	}
}

export const useUIState = (
	api: ExtractInjectionAPI<AIPlugin> | undefined,
): [State, React.Dispatch<Action>] => {
	const [state, dispatch] = useReducer(reducer, initialState);
	const { nextSessionId, status } = useSharedPluginStateWithSelector(
		api,
		['aiExperience'],
		({ aiExperienceState }) => {
			return {
				nextSessionId: aiExperienceState?.aiDecorationExperiencePluginState.nextSessionId,
				status: aiExperienceState?.aiSessionPluginState?.status,
			};
		},
	);

	useEffect(() => {
		if (!nextSessionId) {
			dispatch({ type: 'reset' });
		}
	}, [nextSessionId]);

	if (nextSessionId && fg('platform_editor_ai_migrate_from_xstate')) {
		if (status === 'error') {
			return ['error', dispatch];
		}
	}

	if (nextSessionId && !state.terminated) {
		if (state.discard) {
			return ['discard', dispatch];
		}
		return ['loading', dispatch];
	}
	return ['idle', () => {}];
};
